import { Checkbox, Icon, Loader, Tooltip } from 'kui';
import * as React from 'react';
import { blockMarkdown } from '../../../../../../../../helper/markdownHelper';
import { getMessages } from '../../../../../../../../store/constants';
import { Util } from '../../../../../../../../util/util';
import {
    CARD_DUE_DATE,
    CARD_START_DATE
} from '../../../../../../aside_panel/cardDetails/DatesSection/components/CardDatesSection/constants';
import {
    CARD_ESTIMATE_LABEL_TEXT
} from '../../../../../../aside_panel/cardDetails/EstimateAndSpentTimeSection/components/CardEstimateAndSpentTimeSection/constants';
import { convertTimeNumberToString } from '../../../../../../aside_panel/cardDetails/helpers/convertTimeNumberToString';
import { UsersTooltip } from '../../../../../../base/components/UsersTooltip/UsersTooltip';
import { CARD_CLASS, CARD_CONTROL_CLASS } from '../../../../constants';
import { CHECKLIST_TOOLTIP, CHECKLISTS_TITLE } from '../../../../wording';
import { ICardChecklistItem, ICardChecklistsProps } from './types';
import './_CardChecklists.scss';
import {
    CARD_CHECKLIST_DONE
} from '../../../../../../aside_panel/cardDetails/ChecklistSection/components/CardChecklist/constants';
import ReactHtmlParser from 'react-html-parser';
import { AnalyzeRender } from '../../../../../../helpers/memoizeHelper';
import { onImgLoad } from '../../../helpers/onImgLoad';
// @ts-ignore
const analyzeRender = window.Settings.development ? new AnalyzeRender(`CardChecklists`) : null;

export function CardChecklists ({
    cardId,
    authUserPermissionId,
    checklists,
    className,
    isReadonly,
    boardUsers,
    workHoursInDay,
    setCardHeight,
    onDidMount,
    onHeightChange,
    onToggle,
    onToggleLoadingImg,
}: ICardChecklistsProps) {
    if (analyzeRender) analyzeRender.call(cardId, arguments[0]);
    if (checklists && !checklists.length) return null;

    const classElement = CARD_CLASS + '__checklists';
    const classChecklist = CARD_CLASS + '__checklist';
    const classTitle = classChecklist + '-title';
    const classTitleName = classChecklist + '-title-name';
    const classProgress = classChecklist + '-progress';

    React.useEffect(() => {
        if (setCardHeight) {
            setCardHeight();
        }
    }, [checklists]);

    React.useEffect(() => {
        onDidMount();
    }, []);

    return (
        <div
            className={`
                ${className}
                ${classElement}
                ${checklists && checklists.length === 1 ? classElement + '--single' : ''}
            `}
        >
            {checklists && checklists.length > 1 && <div className={classTitleName}>{CHECKLISTS_TITLE}</div>}
            {checklists
                ? checklists.map(checklist => {
                    return <div
                        className={classChecklist}
                        key={checklist.id}
                    >
                        <div className={classTitle}>
                            <Tooltip
                                direction={'up-right'}
                                isNoEvents
                                isNoWrap
                                value={CHECKLIST_TOOLTIP}
                            >
                                <div className={classTitleName}>
                                    {checklist.name}
                                </div>
                            </Tooltip>
                            {checklist.stats && !!checklist.stats.totalCount &&
                                <div className={classProgress}>
                                    {
                                        getMessages().getText(
                                            CARD_CHECKLIST_DONE,
                                            null,
                                            checklist.stats.checkedPercent,
                                            checklist.stats.checkedCount,
                                            checklist.stats.totalCount
                                        )
                                    }
                                </div>
                            }
                        </div>
                        {!!checklist.checkItems.length && checklist.checkItems.map(item => {
                            return <Item
                                {...item}
                                authUserPermissionId={authUserPermissionId}
                                boardUsers={boardUsers}
                                isReadonly={isReadonly}
                                workHoursInDay={workHoursInDay}
                                onToggle={onToggle}
                                isShowWeights={checklist.isShowWeights}
                                onHeightChange={onHeightChange}
                                onToggleLoadingImg={onToggleLoadingImg}
                                key={item.id}
                            />
                        })}
                    </div>
                })
                : <Loader />
            }
        </div>
    );
}

function Item ({
    assignees,
    authUserPermissionId,
    boardUsers,
    checked,
    checklistId,
    dueDate,
    estimate,
    id,
    isReadonly,
    isShowWeights,
    name,
    startDate,
    weight,
    workHoursInDay,
    onHeightChange,
    onToggle,
    onToggleLoadingImg,
}: ICardChecklistItem) {
    const classItem = CARD_CLASS + '__checklist-item';
    const classItemChecked = classItem + '--checked';
    const classIndicators = classItem + '-indicators';
    const classStartIndicator = classItem + '-startdate-indicator';
    const classDueIndicator = classItem + '-duedate-indicator';
    const classDueIndicatorOverdue = classDueIndicator + '--overdue';
    const classEstimateIndicator = classItem + '-estimate-indicator';

    const assigneesTooltip = assignees && assignees.length
        ? <UsersTooltip
            authUserPermissionId={authUserPermissionId}
            users={assignees}
            boardUsers={boardUsers}
        />
        : null;

    const startDateIndicator = !startDate ? null : (
        <Tooltip
            isNoEvents
            isNoWrap
            value={CARD_START_DATE + ': ' + Util.formatDateMoment(startDate)}
        >
            <div className={classStartIndicator}>
                <Icon size={16} xlink={'start-date'} />
                {Util.formatTimestampToDayMonth(startDate)}
            </div>
        </Tooltip>
    );

    const ref = React.useRef(null);
    const [markdown, setMarkdown] = React.useState('');
    React.useEffect(() => {
        setMarkdown(blockMarkdown(name));
    }, [name]);

    React.useEffect(() => {
        if (!onHeightChange || !markdown || !ref.current) return;
        const cleanup = onImgLoad(ref.current, onHeightChange, onToggleLoadingImg);
        return cleanup;
    }, [markdown]);

    const isItemOverdue = (
        !!dueDate &&
        !checked &&
        Date.now() > dueDate * 1000
    );
    const dueDateIndicator = !dueDate ? null : (
        <Tooltip
            isNoEvents
            isNoWrap
            value={CARD_DUE_DATE + ': ' + Util.formatDateMoment(dueDate)}
        >
            <div className={`
                    ${classDueIndicator}
                    ${isItemOverdue ? classDueIndicatorOverdue : ''}
                `}>
                <Icon size={16} xlink={isItemOverdue ? 'overdue' : 'due-date'} />
                {Util.formatTimestampToDayMonth(dueDate)}
            </div>
        </Tooltip>
    );

    const estimateStr = !estimate ? null : convertTimeNumberToString(estimate, false, workHoursInDay);
    const estimateIndicator = !estimate ? null : (
        <Tooltip
            isNoEvents
            isNoWrap
            value={CARD_ESTIMATE_LABEL_TEXT + ': ' + estimateStr}
        >
            <div className={classEstimateIndicator}>
                <Icon size={16} xlink={'estimate'} />
                {estimateStr}
            </div>
        </Tooltip>
    );

    const weightButton = isShowWeights
        ? <div
            className={classItem + '-weight'}
        >
            <Icon size={16} xlink={'weight'} />
            {weight}
        </div>
        : null;

    return (
        <div
            className={`
                    ${classItem}
                    ${checked ? classItemChecked : ''}
                `}
        >
            <Checkbox
                className={CARD_CONTROL_CLASS}
                checked={checked}
                direction={'left'}
                disabled={isReadonly}
                onChange={() => {onToggle(checklistId, id)}}
            />
            {(!!startDateIndicator || !!dueDateIndicator || !!estimateIndicator || !!assigneesTooltip || !!weightButton) &&
                <div
                    className={classIndicators}
                >
                    {startDateIndicator}
                    {dueDateIndicator}
                    {estimateIndicator}
                    {assigneesTooltip}
                    {weightButton}
                </div>
            }
            <div className={classItem + '-name'} ref={ref}>
                {ReactHtmlParser(markdown)}
            </div>
        </div>
    );
}
